import { phone as parsePhone } from "phone";
import { stripHtmlToText } from "@salestrak/utilities";
import { z } from "zod";

import { currency } from "./currencies.ts";
import { FIELD_TYPES } from "./field-types.ts";
import type { CustomFieldSpec } from "./spec.ts";

/** Per-type length caps from the product rule sheet.
 *
 *  Two gates read this and they must agree: the UI gate (`<Input maxLength>`)
 *  and the commit gate (the schemas below). `phone` and `address` are UI-only —
 *  each stores an object, so a character cap cannot apply to the stored value. */
export const CUSTOM_FIELD_MAX_LENGTHS: Readonly<
  Record<string, number | undefined>
> = {
  text: 255,
  paragraph: 50000,
  email: 254,
  phone: 25,
  url: 2000,
  address: 500,
};

/** The form layer supplies `countryCode` as either a dial code (`"+1"`, the
 *  form half of the pair) or an ISO country (`"US"`, the stored half), and
 *  neither is rejected: a dial code prefixes the number, an ISO country goes
 *  through as the parser's country option. Mobile-prefix checking stays off —
 *  it rejects real landlines, which a CRM must accept. */
function isValidPhone(value: string, countryCode: string | null | undefined): boolean {
  const hint = countryCode ?? "";
  if (hint.startsWith("+")) {
    return parsePhone(`${hint}${value}`, { validateMobilePrefix: false }).isValid;
  }
  // With no country hint the key stays absent: `phone` reads an explicit
  // `undefined` country as a lookup miss rather than as "detect it".
  const options = hint
    ? { country: hint, validateMobilePrefix: false }
    : { validateMobilePrefix: false };
  return parsePhone(value, options).isValid;
}

const optionalText = z.string().nullish();

const capped = (cap: number | undefined) =>
  cap === undefined
    ? z.string()
    : z.string().max(cap, { error: `Must be no more than ${cap} characters` });

/** A number as the form layer produces it: already a number, a numeric string,
 *  or empty. Anything unparseable becomes `null` — "unanswered" — never `NaN`,
 *  which silently defeats every comparison a min or max check makes. */
const numericValue = z.unknown().transform((raw) => {
  if (raw === undefined || raw === null) return raw;
  if (typeof raw === "number") return Number.isNaN(raw) ? null : raw;
  const text = String(raw).trim();
  if (text === "") return null;
  const parsed = Number(text);
  return Number.isNaN(parsed) ? null : parsed;
});

/** The schema one custom field's value must satisfy.
 *
 *  Every field type stores "unanswered" as `null` or `undefined`, so each schema
 *  is nullish on the outside and its checks apply only to a real value. */
export function customFieldSchema(field: CustomFieldSpec): z.ZodType {
  const name = field.name?.trim() || "This field";
  const cap = CUSTOM_FIELD_MAX_LENGTHS[field.type];

  switch (field.type) {
    case FIELD_TYPES.money: {
      const format = currency(field.config?.currency)?.regex;
      const pattern = format ? new RegExp(format) : /.*/;
      return z
        .string()
        // An empty money field is unanswered, not malformed.
        .refine((value) => value === "" || pattern.test(value), {
          error: "Invalid money format",
        })
        .nullish();
    }

    case FIELD_TYPES.phone:
      return z
        .looseObject({ value: optionalText, countryCode: optionalText })
        .refine(
          (parts) => !parts.value || isValidPhone(parts.value, parts.countryCode),
          { error: "Invalid phone number" },
        )
        .nullish();

    case FIELD_TYPES.address:
      return z
        .looseObject({
          addressLine1: optionalText,
          city: optionalText,
          state: optionalText,
          zip: optionalText,
          country: optionalText,
        })
        .nullish();

    case FIELD_TYPES.email:
      return capped(cap)
        // Clearing an email leaves "", which is unanswered, not invalid.
        .refine((value) => value === "" || z.email().safeParse(value).success, {
          error: "Must be a valid email address",
        })
        .nullish();

    case FIELD_TYPES.url:
      return capped(cap)
        .refine(
          (value) => {
            if (value === "") return true;
            try {
              const { protocol } = new URL(value);
              return protocol === "http:" || protocol === "https:";
            } catch {
              return false;
            }
          },
          { error: "Must be a valid URL" },
        )
        .nullish();

    case FIELD_TYPES.number: {
      const { min, max } = field.config ?? {};
      let schema = numericValue;
      if (typeof min === "number") {
        schema = schema.refine((value) => value == null || value >= min, {
          error: `${name} must be at least ${min}`,
        });
      }
      if (typeof max === "number") {
        schema = schema.refine((value) => value == null || value <= max, {
          error: `${name} must be no more than ${max}`,
        });
      }
      return schema;
    }

    case FIELD_TYPES.date:
    case FIELD_TYPES.dateTime:
    case FIELD_TYPES.time:
      // The form layer hands over a Date; a stored value arrives as an ISO
      // string. Both are legitimate input, so the schema coerces.
      return z.coerce.date().nullish();

    case FIELD_TYPES.select:
    case FIELD_TYPES.member:
      // Both persist an array of ids, single-select mode included.
      return z.array(z.string()).nullish();

    case FIELD_TYPES.checkbox:
      // Tri-state: null is unanswered and is not the same as false.
      return z.boolean().nullish();

    case FIELD_TYPES.paragraph:
      return z
        .string()
        // Stored as HTML. Cap the VISIBLE text, so markup does not consume the
        // budget and a short but heavily formatted answer still commits.
        .refine(
          (value) => cap === undefined || stripHtmlToText(value).length <= cap,
          { error: `Must be no more than ${cap} characters` },
        )
        .nullish();

    case FIELD_TYPES.text:
      return capped(cap).nullish();

    default:
      // `files`, `photos` and `region` carry no character or shape constraint.
      // Validation must pass for them rather than fail closed.
      return z.unknown();
  }
}

export type CustomFieldValidationResult = {
  valid: boolean;
  message: string | null;
};

/** Validates one value and returns the first problem as a display string.
 *
 *  `ZodError.issues` is the only issue list in zod 4 — the v3 `.errors` alias is
 *  gone, and `.message` holds a JSON dump of the issues, not a sentence. */
export function validateCustomFieldValue(
  field: CustomFieldSpec,
  value: unknown,
): CustomFieldValidationResult {
  const result = customFieldSchema(field).safeParse(value);
  if (result.success) return { valid: true, message: null };
  return {
    valid: false,
    message: result.error.issues[0]?.message ?? "Invalid value",
  };
}
